import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Popup, CircleMarker, useMap } from 'react-leaflet';
import { LatLngBounds } from 'leaflet';
import { db } from '../firebase';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Papa from 'papaparse';
import 'leaflet/dist/leaflet.css';
import { COMMB_SAMPLE_LOCATIONS, CommbLocation } from '../data/commbSample';

// A point on the map, from either our own inventory or the COMMB layer.
interface MapPoint {
  id: string;
  source: 'inventory' | 'commb';
  operatorCompany: string;
  assetType: string;
  status: string;
  latitude: number;
  longitude: number;
  address: string;
  market: string;
  dailyImpression?: number;
  width?: number;
  height?: number;
  isSample?: boolean;
}

interface InventoryGroup {
  id: string;
  name: string;
  ids: string[];
}

const SOURCE_COLORS: Record<MapPoint['source'], string> = {
  inventory: '#E10600', // brand red — our own inventory
  commb: '#3A3A3A', // dark grey — COMMB layer
};

const MAP_LAYERS: Record<string, { label: string; url: string; attribution: string; subdomains?: string }> = {
  positron: {
    label: 'Light (CARTO Positron)',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
  },
  dark: {
    label: 'Dark (CARTO Dark Matter)',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
  },
  osm: {
    label: 'Streets (OpenStreetMap)',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    subdomains: 'abc',
  },
  esri: {
    label: 'Satellite (Esri World Imagery)',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
  },
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value ?? '').trim());
  return isNaN(parsed) ? NaN : parsed;
};

// Valid, non-degenerate coordinates. (0,0) means "never geocoded".
const hasValidCoords = (lat: number, lng: number): boolean =>
  isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 && !(lat === 0 && lng === 0);

// Fits the viewport to the currently visible points whenever they change.
const FitBounds: React.FC<{ points: MapPoint[] }> = ({ points }) => {
  const map = useMap();
  const lastKey = useRef('');
  useEffect(() => {
    if (points.length === 0) return;
    const key = points.map(p => p.id).join(',');
    if (key === lastKey.current) return;
    lastKey.current = key;
    const bounds = new LatLngBounds(points.map(p => [p.latitude, p.longitude] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
};

// Header names accepted by the COMMB CSV importer (case-insensitive).
const COMMB_CSV_FIELDS: Record<string, keyof CommbLocation> = {
  'operator company': 'operatorCompany',
  'operator': 'operatorCompany',
  'type of inventory': 'assetType',
  'asset type': 'assetType',
  'type': 'assetType',
  'latitude': 'latitude',
  'lat': 'latitude',
  'longitude': 'longitude',
  'lng': 'longitude',
  'lon': 'longitude',
  'address': 'address',
  'location': 'address',
  'market': 'market',
  'city': 'market',
};

const InventoryMap: React.FC = () => {
  const [assets, setAssets] = useState<MapPoint[]>([]);
  const [commbLocations, setCommbLocations] = useState<MapPoint[]>([]);
  const [commbIsSample, setCommbIsSample] = useState(false);
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');

  const [layer, setLayer] = useState<keyof typeof MAP_LAYERS>('positron');
  const [sourceFilter, setSourceFilter] = useState<'both' | 'inventory' | 'commb'>('both');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [operatorFilter, setOperatorFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const commbToPoint = (loc: CommbLocation): MapPoint => ({
    id: loc.id,
    source: 'commb',
    operatorCompany: loc.operatorCompany || '',
    assetType: loc.assetType || '',
    status: 'active',
    latitude: toNumber(loc.latitude),
    longitude: toNumber(loc.longitude),
    address: loc.address || '',
    market: loc.market || '',
    isSample: !!loc.isSample,
  });

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [assetSnap, groupSnap, commbSnap] = await Promise.all([
        getDocs(collection(db, 'Assets')),
        getDocs(collection(db, 'InventoryGroups')),
        getDocs(collection(db, 'CommbLocations')),
      ]);

      setAssets(
        assetSnap.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            source: 'inventory' as const,
            operatorCompany: data.operatorCompany || '',
            assetType: data.assetType || '',
            status: data.status || '',
            latitude: toNumber(data.latitude),
            longitude: toNumber(data.longitude),
            address: data.address || '',
            market: '',
            dailyImpression: toNumber(data.dailyImpression) || undefined,
            width: toNumber(data.width) || undefined,
            height: toNumber(data.height) || undefined,
          };
        })
      );

      setGroups(groupSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as InventoryGroup)));

      if (commbSnap.empty) {
        // No licensed COMMB data imported yet — fall back to the bundled sample.
        setCommbLocations(COMMB_SAMPLE_LOCATIONS.map(commbToPoint));
        setCommbIsSample(true);
      } else {
        setCommbLocations(
          commbSnap.docs.map(docSnap => commbToPoint({ id: docSnap.id, ...docSnap.data() } as CommbLocation))
        );
        setCommbIsSample(false);
      }
    } catch (err) {
      console.error('Failed to load map data:', err);
      setLoadError('Failed to load map data from the database.');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCommbImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportMessage('');
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results: Papa.ParseResult<Record<string, string>>) => {
        try {
          let imported = 0;
          let skipped = 0;
          for (const row of results.data) {
            const record: Partial<CommbLocation> = {};
            Object.entries(row).forEach(([key, value]) => {
              const field = COMMB_CSV_FIELDS[key.trim().toLowerCase()];
              if (field && field !== 'latitude' && field !== 'longitude' && field !== 'id' && field !== 'isSample') {
                (record as Record<string, string>)[field] = String(value ?? '').trim();
              }
            });
            const lat = toNumber(row[Object.keys(row).find(k => ['latitude', 'lat'].includes(k.trim().toLowerCase())) ?? '']);
            const lng = toNumber(row[Object.keys(row).find(k => ['longitude', 'lng', 'lon'].includes(k.trim().toLowerCase())) ?? '']);
            if (!hasValidCoords(lat, lng)) {
              skipped++;
              continue;
            }
            const id = `commb-${Date.now()}-${imported}`;
            await setDoc(doc(db, 'CommbLocations', id), {
              ...record,
              latitude: lat,
              longitude: lng,
              importedAt: new Date().toISOString(),
            });
            imported++;
          }
          setImportMessage(
            `Imported ${imported} COMMB location(s)${skipped ? `, skipped ${skipped} row(s) without valid coordinates` : ''}.`
          );
          await loadData();
        } catch (err) {
          console.error('COMMB import failed:', err);
          setImportMessage('COMMB import failed — check the browser console for details.');
        }
        setImporting(false);
      },
      error: () => {
        setImportMessage('Could not parse the CSV file.');
        setImporting(false);
      },
    });
  };

  const selectedGroup = selectedGroupId === 'all' ? null : groups.find(g => g.id === selectedGroupId);

  const allPoints = useMemo(() => [...assets, ...commbLocations], [assets, commbLocations]);

  const operators = useMemo(
    () => Array.from(new Set(allPoints.map(p => p.operatorCompany).filter(Boolean))).sort(),
    [allPoints]
  );
  const assetTypes = useMemo(
    () => Array.from(new Set(allPoints.map(p => p.assetType).filter(Boolean))).sort(),
    [allPoints]
  );

  const invalidCount = useMemo(
    () => allPoints.filter(p => !hasValidCoords(p.latitude, p.longitude)).length,
    [allPoints]
  );

  const visiblePoints = useMemo(
    () =>
      allPoints.filter(p => {
        if (!hasValidCoords(p.latitude, p.longitude)) return false;
        if (sourceFilter !== 'both' && p.source !== sourceFilter) return false;
        if (p.source === 'inventory' && selectedGroup && !selectedGroup.ids.includes(p.id)) return false;
        if (operatorFilter !== 'all' && p.operatorCompany !== operatorFilter) return false;
        if (typeFilter !== 'all' && p.assetType !== typeFilter) return false;
        return true;
      }),
    [allPoints, sourceFilter, selectedGroup, operatorFilter, typeFilter]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%" gap={2}>
        <CircularProgress size={24} />
        <Typography>Loading map…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ width: '100%', zIndex: 1000, px: 2, pt: 2, pb: 1, flexWrap: 'wrap', rowGap: 1.5 }}
        useFlexGap
        alignItems="center"
      >
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="source-select-label">Data Source</InputLabel>
          <Select
            labelId="source-select-label"
            value={sourceFilter}
            label="Data Source"
            onChange={e => setSourceFilter(e.target.value as 'both' | 'inventory' | 'commb')}
          >
            <MenuItem value="both">My Inventory + COMMB</MenuItem>
            <MenuItem value="inventory">My Inventory</MenuItem>
            <MenuItem value="commb">COMMB</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="group-select-label">Group</InputLabel>
          <Select
            labelId="group-select-label"
            value={selectedGroupId}
            label="Group"
            onChange={e => setSelectedGroupId(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {groups.map(group => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="operator-select-label">Operator</InputLabel>
          <Select
            labelId="operator-select-label"
            value={operatorFilter}
            label="Operator"
            onChange={e => setOperatorFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {operators.map(op => (
              <MenuItem key={op} value={op}>
                {op}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="type-select-label">Asset Type</InputLabel>
          <Select labelId="type-select-label" value={typeFilter} label="Asset Type" onChange={e => setTypeFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            {assetTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="map-layer-label">Map Layer</InputLabel>
          <Select
            labelId="map-layer-label"
            value={layer}
            label="Map Layer"
            onChange={e => setLayer(e.target.value as keyof typeof MAP_LAYERS)}
          >
            {Object.entries(MAP_LAYERS).map(([key, val]) => (
              <MenuItem key={key} value={key}>
                {val.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <input type="file" accept=".csv" id="commb-import-input" style={{ display: 'none' }} onChange={handleCommbImport} />
        <label htmlFor="commb-import-input">
          <Button
            variant="outlined"
            size="small"
            component="span"
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            disabled={importing}
          >
            Import COMMB CSV
          </Button>
        </label>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1, flexWrap: 'wrap', rowGap: 1 }} useFlexGap alignItems="center">
        <Chip size="small" label={`${visiblePoints.length} location(s) shown`} />
        <Chip
          size="small"
          sx={{ bgcolor: SOURCE_COLORS.inventory, color: '#fff' }}
          label={`My Inventory: ${visiblePoints.filter(p => p.source === 'inventory').length}`}
        />
        <Chip
          size="small"
          sx={{ bgcolor: SOURCE_COLORS.commb, color: '#fff', border: '1px solid #fff' }}
          label={`COMMB: ${visiblePoints.filter(p => p.source === 'commb').length}`}
        />
        {commbIsSample && (
          <Chip
            size="small"
            variant="outlined"
            label="COMMB layer: bundled sample data — use Import COMMB CSV to load licensed data"
          />
        )}
        {invalidCount > 0 && (
          <Chip size="small" variant="outlined" color="warning" label={`${invalidCount} location(s) hidden (missing/invalid coordinates)`} />
        )}
      </Stack>

      {(loadError || importMessage) && (
        <Alert severity={loadError ? 'error' : 'info'} sx={{ mx: 2, mb: 1 }} onClose={() => setImportMessage('')}>
          {loadError || importMessage}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MapContainer center={[43.6532, -79.3832]} zoom={11} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            key={layer}
            url={MAP_LAYERS[layer].url}
            attribution={MAP_LAYERS[layer].attribution}
            {...(MAP_LAYERS[layer].subdomains ? { subdomains: MAP_LAYERS[layer].subdomains } : {})}
          />
          <FitBounds points={visiblePoints} />
          {visiblePoints.map(point => (
            <CircleMarker
              key={`${point.source}-${point.id}`}
              center={[point.latitude, point.longitude]}
              radius={point.source === 'inventory' ? 8 : 6}
              pathOptions={{
                color: '#ffffff',
                weight: 1.5,
                fillColor: SOURCE_COLORS[point.source],
                fillOpacity: 0.9,
              }}
            >
              <Popup>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" sx={{ color: '#1A1A2E', fontWeight: 700, mb: 0.5 }}>
                    {point.source === 'inventory' ? `Asset ${point.id}` : point.operatorCompany || 'COMMB location'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>
                    Source: {point.source === 'inventory' ? 'My Inventory' : point.isSample ? 'COMMB (sample)' : 'COMMB'}
                  </Typography>
                  {point.operatorCompany && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Operator: {point.operatorCompany}
                    </Typography>
                  )}
                  {point.assetType && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Type: {point.assetType}
                    </Typography>
                  )}
                  {point.source === 'inventory' && point.status && (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Status: {point.status}
                    </Typography>
                  )}
                  {point.width && point.height ? (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Dimensions: {point.height}x{point.width} ft
                    </Typography>
                  ) : null}
                  {point.dailyImpression ? (
                    <Typography variant="body2" sx={{ color: '#333' }}>
                      Daily Impressions: {point.dailyImpression.toLocaleString()}
                    </Typography>
                  ) : null}
                  {(point.address || point.market) && (
                    <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
                      {[point.address, point.market].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Box>
    </Box>
  );
};

export default InventoryMap;
